/**
 * 腾讯域：T0广告看板 - 看板数据 API 服务（CLAUDE.md §前端 API 服务层 — 按域分目录）。
 *
 * 与后端 Pydantic 模型 src.tencent.dtos.t0_ad_board 对齐。
 *   - GET /api/v1/tencent/t0-ad-board/options → 筛选条选项（代理商 / 时间节点）
 *   - GET /api/v1/tencent/t0-ad-board/data    → 数据合计 + 数据详情
 *
 * 数据源 tx_t0_ad_board_latest（auto_strategy 第 5 跳分钟级快照写入）。
 */

import { request } from "../apiClient";

/** 「新客大盘」虚拟代理商选项值（与后端 T0_AD_BOARD_ALL_DAILI 对齐）：
 *  选中时后端汇总全部名称解析代理（YK/YR/PR/LO 等）、排除自定义账户集合行
 *  （集合与代理解析的账户可重叠，不排除会复算）；options 首项恒为该值，页面默认选中。 */
export const T0_AD_BOARD_ALL_DAILI = "新客大盘";

/** 筛选条选项（与后端 T0AdBoardOptions 对齐）。 */
export interface T0AdBoardOptions {
  daili_list: string[];
  data_time_list: string[];
}

/** 数据合计行（与后端 T0AdBoardSummary 对齐；比率分母 0 为 null）。 */
export interface T0AdBoardSummary {
  t0_qy_feilv: number | null;
  t0_qyjj_per: number | null;
  t0_credit_ratio: number | null;
  t0_credit_per_amt: number | null;
  cost: number;
  t0_qyjj: number;
  t0_qy_credit: number;
  t0_qy_credit_amt: number;
  apply_pv: number;
  reservation_uv: number;
  resv_apply_ratio: number | null;
}

/** 数据详情单行（与后端 T0AdBoardDetailRow 对齐）。 */
export interface T0AdBoardDetailRow {
  account_id: string;
  ad_id: string;
  adgroup_name: string;
  t0_qy_feilv: number | null;
  t0_qyjj_per: number | null;
  t0_credit_ratio: number | null;
  t0_credit_per_amt: number | null;
  cost: number;
  t0_qyjj: number;
  t0_qy_credit: number;
  t0_qy_credit_amt: number;
  apply_pv: number;
  reservation_uv: number;
  resv_apply_ratio: number | null;
}

/** 数据详情·账户粒度单行（与后端 T0AdBoardAccountRow 对齐：账户ID + 11 指标，
 *  对该账户下全部广告聚合；比率 = SUM(x)/SUM(y) 合计相除，分母 0 为 null）。 */
export interface T0AdBoardAccountRow {
  account_id: string;
  t0_qy_feilv: number | null;
  t0_qyjj_per: number | null;
  t0_credit_ratio: number | null;
  t0_credit_per_amt: number | null;
  cost: number;
  t0_qyjj: number;
  t0_qy_credit: number;
  t0_qy_credit_amt: number;
  apply_pv: number;
  reservation_uv: number;
  resv_apply_ratio: number | null;
}

/** 看板数据出参（与后端 T0AdBoardData 对齐：合计 + 广告/账户双粒度明细）。 */
export interface T0AdBoardData {
  summary: T0AdBoardSummary | null;
  rows: T0AdBoardDetailRow[];
  is_truncated: boolean;
  account_rows: T0AdBoardAccountRow[];
  is_account_truncated: boolean;
}

/**
 * 看板数据查询入参（与后端 T0AdBoardQuery 对齐）。
 * daili / data_time 必选（页面无「全部」）；account_id / ad_id 可选（空 = 全部）。
 * 用 `type` 而非 `interface`：TS 中只有 type 别名可隐式满足 apiClient 的
 * `Record<string, ...>` 索引签名（interface 不行）。
 */
export type T0AdBoardQuery = {
  daili: string;
  data_time: string;
  account_id?: string;
  ad_id?: string;
};

/** 取筛选条选项（代理商 = 首项恒为「新客大盘」+ 最新时点去重 daili；时间节点 = 最近 7 天各时点倒序）。 */
export async function getT0AdBoardOptions(): Promise<T0AdBoardOptions> {
  const data = await request<T0AdBoardOptions | null>(
    "/api/v1/tencent/t0-ad-board/options",
  );
  return data ?? { daili_list: [], data_time_list: [] };
}

/** 取看板数据（合计 + 明细一次返回）。 */
export async function getT0AdBoardData(
  query: T0AdBoardQuery,
): Promise<T0AdBoardData> {
  const data = await request<T0AdBoardData | null>(
    "/api/v1/tencent/t0-ad-board/data",
    { query },
  );
  return data ?? {
    summary: null,
    rows: [],
    is_truncated: false,
    account_rows: [],
    is_account_truncated: false,
  };
}
